import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useGetPlanLimitsQuery } from '@/store/api/apiSlice';
import type { PlanLimits } from '@/hooks/useFeatureAccess';
import { UserRole } from '@/types';
import { FiLock } from 'react-icons/fi';

type FeatureKey = keyof PlanLimits['features'];

const FEATURE_LABELS: Partial<Record<FeatureKey, string>> = {
  advancedReporting: 'Schedule, Workload, Reports & Insights',
  integrations: 'Integrations',
  webhooks: 'Webhooks',
  apiAccess: 'Workflows & API',
  customRoles: 'Custom roles',
  sso: 'Single sign-on',
};

/**
 * Route-level plan gate — uses the same `/usage/limits` payload as the sidebar.
 * SUPER_ADMIN bypasses; others see an upgrade prompt when the feature is locked.
 */
export default function PlanFeatureGuard({
  feature,
  children,
}: {
  feature: FeatureKey;
  children: JSX.Element;
}) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { data: planLimitsData, isLoading, isError } = useGetPlanLimitsQuery(undefined);

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center bg-gray-50" />
    );
  }

  if (user?.role === UserRole.SUPER_ADMIN) {
    return children;
  }

  const features = planLimitsData?.limits?.features;

  if (isError || !features) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-text-muted">{t('common.planVerifyFailed')}</p>
      </div>
    );
  }

  if (!features[feature]) {
    const tierLabel = planLimitsData?.usageTierLabel || 'your current plan';
    const featureLabel = FEATURE_LABELS[feature] || 'This feature';

    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center px-6 text-center bg-bg">
        <div className="max-w-md rounded-xl border border-border bg-surface p-8 shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
            <FiLock className="h-5 w-5" />
          </div>
          <h2 className="text-base font-bold text-text-primary">{t('common.upgradeRequired')}</h2>
          <p className="mt-2 text-sm text-text-muted">
            {t('common.upgradeRequiredMsg', { feature: featureLabel, plan: tierLabel })}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/billing"
              className="inline-flex items-center rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-dark transition-colors"
            >
              {t('common.viewPlans')}
            </Link>
            <Link
              to="/dashboard"
              className="inline-flex items-center rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-text-secondary hover:bg-surface-hover transition-colors"
            >
              {t('common.backToDashboard')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
